import aiLabelUrl from '../assets/wav/ai_label.wav';

const writeAscii = (view: DataView, offset: number, value: string) => {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
};

const encodePcm16Wav = (audioBuffer: AudioBuffer) => {
  const channelCount = audioBuffer.numberOfChannels;
  const sampleCount = audioBuffer.length;
  const bytesPerSample = 2;
  const dataLength = sampleCount * channelCount * bytesPerSample;
  const output = new ArrayBuffer(44 + dataLength);
  const view = new DataView(output);

  writeAscii(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeAscii(view, 8, 'WAVE');
  writeAscii(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, channelCount, true);
  view.setUint32(24, audioBuffer.sampleRate, true);
  view.setUint32(28, audioBuffer.sampleRate * channelCount * bytesPerSample, true);
  view.setUint16(32, channelCount * bytesPerSample, true);
  view.setUint16(34, 16, true);
  writeAscii(view, 36, 'data');
  view.setUint32(40, dataLength, true);

  const channels = Array.from({ length: channelCount }, (_, index) => audioBuffer.getChannelData(index));
  let offset = 44;
  for (let sampleIndex = 0; sampleIndex < sampleCount; sampleIndex += 1) {
    for (let channelIndex = 0; channelIndex < channelCount; channelIndex += 1) {
      const sample = Math.max(-1, Math.min(1, channels[channelIndex][sampleIndex]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += bytesPerSample;
    }
  }

  return new Blob([output], { type: 'audio/wav' });
};

/** Appends the mandatory AI label audio and returns a new playable/downloadable WAV. */
export const appendAiLabel = async (audioUrl: string) => {
  const [sourceData, labelData] = await Promise.all([
    fetch(audioUrl).then(response => {
      if (!response.ok) throw new Error(`音频加载失败：${response.status}`);
      return response.arrayBuffer();
    }),
    fetch(aiLabelUrl).then(response => {
      if (!response.ok) throw new Error(`AI 标识音频加载失败：${response.status}`);
      return response.arrayBuffer();
    }),
  ]);

  const audioContext = new AudioContext();
  try {
    const [sourceBuffer, labelBuffer] = await Promise.all([
      audioContext.decodeAudioData(sourceData),
      audioContext.decodeAudioData(labelData),
    ]);
    const sampleRate = sourceBuffer.sampleRate;
    const channelCount = Math.max(sourceBuffer.numberOfChannels, labelBuffer.numberOfChannels);
    const frameCount = Math.ceil((sourceBuffer.duration + labelBuffer.duration) * sampleRate);
    const offlineContext = new OfflineAudioContext(channelCount, frameCount, sampleRate);

    const source = offlineContext.createBufferSource();
    source.buffer = sourceBuffer;
    source.connect(offlineContext.destination);
    source.start(0);

    const label = offlineContext.createBufferSource();
    label.buffer = labelBuffer;
    label.connect(offlineContext.destination);
    label.start(sourceBuffer.duration);

    const rendered = await offlineContext.startRendering();
    return encodePcm16Wav(rendered);
  } finally {
    await audioContext.close();
  }
};
